import { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, MoreHorizontal, Instagram, Youtube, Users, Layers, TrendingUp, Edit2, Trash2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useApp, CONTENT_TYPES } from '@/context/AppContext';
import { toast } from 'sonner';

const AVATARS_COLORS = ['hsl(var(--primary))', 'hsl(var(--accent))', 'hsl(var(--success))', 'hsl(var(--warning))', 'hsl(var(--destructive))'];

export default function Creators() {
  const { creators, addCreator, setActiveCreatorId } = useApp();
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const handleAdd = () => {
    if (!name.trim()) return;
    const initials = name.trim().split(' ').map(w => w[0].toUpperCase()).slice(0, 2).join('');
    addCreator({
      id: `c${Date.now()}`,
      name: name.trim(),
      avatar: initials,
      email: email.trim(),
      brands: [],
    });
    setName(''); setEmail('');
    setAddOpen(false);
    toast.success(`Creator "${name.trim()}" added`);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Creators</h2>
          <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--foreground-muted))' }}>
            {creators.length} creator{creators.length !== 1 ? 's' : ''} in your agency
          </p>
        </div>
        <Button size="sm" className="gap-1.5 text-xs" onClick={() => setAddOpen(true)}
          style={{ background: 'hsl(var(--primary))', color: '#fff' }}
        >
          <Plus size={14} /> Add Creator
        </Button>
      </div>

      {/* Creator cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {creators.map((creator, idx) => (
          <motion.div
            key={creator.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.07 }}
            className="reelia-card-glow p-5 flex flex-col"
          >
            {/* Creator header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0"
                  style={{ background: `${AVATARS_COLORS[idx % AVATARS_COLORS.length]}22`, color: AVATARS_COLORS[idx % AVATARS_COLORS.length] }}
                >
                  {creator.avatar}
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'hsl(var(--foreground))' }}>{creator.name}</p>
                  {creator.email && (
                    <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--foreground-subtle))' }}>{creator.email}</p>
                  )}
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 rounded hover:bg-surface-hover" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                    <MoreHorizontal size={15} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setActiveCreatorId(creator.id)}>
                    <Users size={13} className="mr-2" /> Switch to Creator
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Edit2 size={13} className="mr-2" /> Edit Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Mail size={13} className="mr-2" /> Send Invite
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive">
                    <Trash2 size={13} className="mr-2" /> Remove Creator
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[
                { label: 'Brands', value: creator.brands.length, icon: Layers },
                { label: 'Posts', value: Math.floor(Math.random() * 80 + 20), icon: TrendingUp },
                { label: 'Accounts', value: [...new Set(creator.brands.flatMap(b => b.platforms))].length, icon: Users },
              ].map(stat => (
                <div key={stat.label} className="rounded-lg p-2.5" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
                  <stat.icon size={12} style={{ color: 'hsl(var(--foreground-subtle))' }} />
                  <p className="text-base font-semibold mt-1" style={{ color: 'hsl(var(--foreground))' }}>{stat.value}</p>
                  <p className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Brands list */}
            <div className="flex-1">
              <p className="text-xs font-medium mb-2" style={{ color: 'hsl(var(--foreground-subtle))' }}>BRANDS</p>
              <div className="space-y-1.5">
                {creator.brands.length === 0 && (
                  <p className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>No brands yet</p>
                )}
                {creator.brands.map(brand => (
                  <div key={brand.id} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: brand.color }} />
                    <span className="text-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>{brand.name}</span>
                    <span className="text-xs ml-auto" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                      {CONTENT_TYPES.find(ct => ct.id === brand.contentType)?.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-4 text-xs h-8"
              onClick={() => setActiveCreatorId(creator.id)}
              style={{ border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground-muted))' }}
            >
              Manage Creator
            </Button>
          </motion.div>
        ))}

        {/* Add creator card */}
        <button
          onClick={() => setAddOpen(true)}
          className="reelia-card p-5 flex flex-col items-center justify-center gap-3 min-h-48 border-dashed"
          style={{ borderStyle: 'dashed', opacity: 0.6 }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'hsl(var(--surface-elevated))' }}>
            <Plus size={18} style={{ color: 'hsl(var(--foreground-muted))' }} />
          </div>
          <p className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>Add Creator</p>
        </button>
      </div>

      {/* Add Creator Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Creator</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Full Name *</label>
              <input className="reelia-input" placeholder="Creator name" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: 'hsl(var(--foreground-muted))' }}>Email</label>
              <input className="reelia-input" placeholder="creator@example.com" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button size="sm" disabled={!name.trim()} onClick={handleAdd}
              style={{ background: 'hsl(var(--primary))', color: '#fff' }}
            >
              Add Creator
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
